import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { FaExclamationTriangle, FaQuestionCircle, FaRedo } from 'react-icons/fa';
import * as PATH from '../../../CentralisedLink';
import { resendEmailChange } from '../../../Redux/Dashboard/External/EmailMigration/EmailMigrationAction';
import { PENDING_HELIX_EMAIL_KEY } from '../../../Constants/emailMigration';

const RESEND_COOLDOWN_SECONDS = 10;

const EmailVerificationInvalid = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { resendLoading } = useSelector((state) => state.EmailMigration);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownIntervalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        window.clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  const startResendCooldown = () => {
    if (cooldownIntervalRef.current) {
      window.clearInterval(cooldownIntervalRef.current);
    }

    setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
    cooldownIntervalRef.current = window.setInterval(() => {
      setCooldownRemaining((remaining) => {
        if (remaining <= 1) {
          window.clearInterval(cooldownIntervalRef.current);
          cooldownIntervalRef.current = null;
          return 0;
        }

        return remaining - 1;
      });
    }, 1000);
  };

  const handleResendVerificationEmail = async () => {
    if (resendLoading || cooldownRemaining > 0) {
      return;
    }

    const pendingEmail = sessionStorage.getItem(PENDING_HELIX_EMAIL_KEY);

    if (!pendingEmail) {
      enqueueSnackbar('We could not find your pending email. Please start the email update again.', { variant: 'error' });
      navigate(PATH.UPDATE_EMAIL_PATH);
      return;
    }

    startResendCooldown();
    const result = await dispatch(resendEmailChange(pendingEmail));

    if (result.success) {
      enqueueSnackbar('Verification email resent. Please check your inbox.', { variant: 'success' });
      return;
    }

    enqueueSnackbar(result.error || 'Something went wrong. Please try again.', { variant: 'error' });
  };

  return (
    <main className="update-email-page email-verification-invalid-page">
      <section className="update-email-panel" aria-labelledby="email-verification-invalid-title">
        <div className="update-email-hero-icon" aria-hidden="true">
          <FaExclamationTriangle />
        </div>

        <h1 id="email-verification-invalid-title" className="update-email-title">
          Verification Link Invalid
        </h1>
        <p className="update-email-subtitle">
          This verification link has expired or is invalid
        </p>

        <div className="email-verification-invalid-card">
          <div className="email-verification-invalid-box email-verification-error-box">
            <span className="email-verification-invalid-box-icon" aria-hidden="true">
              <FaExclamationTriangle />
            </span>
            <div>
              <h2 className="email-verification-invalid-box-title">Unable to Verify Email</h2>
              <p className="email-verification-invalid-box-text">
                The verification link you clicked is either expired or has already been used.
                Verification links are valid for 24 hours from the time they are sent.
              </p>
              <ul className="email-verification-invalid-list">
                <li>Links expire after 24 hours</li>
                <li>Links can only be used once</li>
                <li>Make sure you're using the most recent link</li>
              </ul>
            </div>
          </div>

          <div className="email-verification-invalid-box email-verification-help-box">
            <span className="email-verification-invalid-box-icon" aria-hidden="true">
              <FaQuestionCircle />
            </span>
            <div>
              <h2 className="email-verification-invalid-box-title">What should I do?</h2>
              <p className="email-verification-invalid-box-text">
                Click the button below to request a new verification email. A fresh link will be sent to your new email address.
              </p>
            </div>
          </div>
        </div>

        <div className="email-verification-invalid-actions" aria-label="Invalid verification actions">
          <button
            type="button"
            className="email-verification-invalid-action"
            onClick={handleResendVerificationEmail}
            disabled={resendLoading || cooldownRemaining > 0}
          >
            <FaRedo aria-hidden="true" />
            {resendLoading
              ? 'Resending...'
              : cooldownRemaining > 0
                ? `Resend Verification Email (${cooldownRemaining}s)`
                : 'Resend Verification Email'}
          </button>
        </div>
      </section>
    </main>
  );
};

export default EmailVerificationInvalid;
